import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

import Button from "shared/components/FormElements/Button";
import SmsSenderAccountSelect, { DEFAULT_SMS_MSG_HEADER } from "shared/components/SmsSenderAccountSelect";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { isInternationalTheme } from "config/ConfigUtils";
import { SMS_STATUS_CODES } from "features/sms/constants";
import { formatDateWithTimezone } from "shared/utility/date";

// Tour driver messaging — uses the SAME smsv2 pipeline as the rest of the app (SmsTable):
//   - loads/polls history via POST /smsv2/getsmsbymodel  (model=personelservice, modelId=tourId)
//   - sends via POST /smsv2                                (real SMS + provider status + record)
// Tours ARE PersonelService docs, so model/modelId tie the SMS history to the tour exactly like
// standard services. Sending requires a SAVED tour (modelId) — enforced via `serviceId`.

const MODEL = "personelservice";
const MAX_SMS_LENGTH = 1000;
const POLL_MS = 10000;

const TourDriverSms = ({ driver, serviceId, defaultMessage = "" }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const [message, setMessage] = useState(defaultMessage);
  const [msgHeader, setMsgHeader] = useState(DEFAULT_SMS_MSG_HEADER);
  const [smses, setSmses] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const timezone = auth?.timezone || "Europe/Istanbul";
  const driverId = driver?.id ? driver.id.toString() : null;
  const hasPhone = !!driver?.phonenumber?.length;
  const canSend = !!driverId && hasPhone && !!serviceId && !isSending;

  // --- Load + poll SMS history for this tour's driver ---
  const loadSmses = useCallback(async () => {
    if (!serviceId || !driverId) {
      setIsLoaded(true);
      return;
    }
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2/getsmsbymodel`,
        "POST",
        JSON.stringify({ model: MODEL, modelId: serviceId }),
      );
      const all = Array.isArray(data?.smses) ? data.smses : [];
      setSmses(all.filter((s) => s?.user?.toString() === driverId));
    } catch (err) {
      // silent — history is best-effort
    }
    setIsLoaded(true);
  }, [serviceId, driverId, sendRequest]);

  const loadRef = useRef(loadSmses);
  useEffect(() => {
    loadRef.current = loadSmses;
  }, [loadSmses]);

  useEffect(() => {
    if (!serviceId) {
      setIsLoaded(true);
      return undefined;
    }
    loadRef.current();
    const interval = setInterval(() => loadRef.current(), POLL_MS);
    return () => clearInterval(interval);
  }, [serviceId]);

  // --- Send ---
  const handleSend = useCallback(async () => {
    const text = message.trim();
    if (!text) {
      window.toast?.error(t("tourBuilder.messageEmpty"));
      return;
    }
    if (!serviceId) {
      window.toast?.error(t("tourBuilder.smsSaveFirst"));
      return;
    }
    if (!hasPhone) {
      window.toast?.error(t("tourBuilder.smsNoPhone"));
      return;
    }

    setIsSending(true);
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2`,
        "POST",
        JSON.stringify({
          user: driverId,
          phoneNumber: driver.phonenumber,
          text,
          model: MODEL,
          modelId: serviceId,
          ...(!isInternationalTheme() && { msgHeader }),
        }),
      );
      if (data?.sms) {
        setSmses((prev) => [...prev, data.sms]);
        window.toast?.success(t("tourBuilder.notifySent"));
      }
    } catch (err) {
      if (err?.message) window.toast?.error(err.message);
      else window.toast?.error(t("actionFailed"));
    }
    setIsSending(false);
  }, [message, serviceId, hasPhone, driverId, driver, msgHeader, sendRequest, t]);

  const renderSmsStatus = (sms) => {
    if (sms.status === SMS_STATUS_CODES.DELIVERED && sms.deliveredDate) {
      return <span>{formatDateWithTimezone(sms.deliveredDate, timezone)}</span>;
    }
    if (sms.status === SMS_STATUS_CODES.QUEUED) {
      return <span style={{ color: "#c0392b" }}>{t("smsNotReachedYet")}</span>;
    }
    return <span style={{ color: "#c0392b" }}>{t("smsCouldNotBeSent")}</span>;
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{t("tourBuilder.notifyHint")}</div>

      {!isInternationalTheme() && (
        <div style={{ marginBottom: 10, maxWidth: 320 }}>
          <SmsSenderAccountSelect value={msgHeader} onChange={setMsgHeader} disabled={isSending} />
        </div>
      )}

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={MAX_SMS_LENGTH}
        placeholder={t("typeYourMessage")}
        style={{
          width: "100%",
          padding: 12,
          border: "1px solid #ccc",
          borderRadius: 8,
          fontSize: 14,
          fontFamily: "inherit",
          resize: "vertical",
          minHeight: 90,
          boxSizing: "border-box",
        }}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        <span style={{ fontSize: 12, color: "#c0392b" }}>
          {!driverId
            ? t("tourBuilder.validation.driver")
            : !serviceId
            ? t("tourBuilder.smsSaveFirst")
            : !hasPhone
            ? t("tourBuilder.smsNoPhone")
            : ""}
        </span>
        <Button type="button" danger onClick={handleSend} disabled={!canSend} isLoadingFromLeft={isSending}>
          {t("sendSmsToDriver")}
        </Button>
      </div>

      {serviceId && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t("sentMessages")}</div>
          {smses.length === 0 ? (
            <div style={{ fontSize: 12, color: "#999" }}>{isLoaded ? t("noMessage") : "…"}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
              {[...smses].reverse().map((sms) => (
                <div
                  key={sms.id}
                  style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 13, marginBottom: 4 }}>{sms.text}</div>
                  <div style={{ fontSize: 11, color: "#888", textAlign: "right" }}>{renderSmsStatus(sms)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TourDriverSms;
